// client/src/components/ticketing/RegistrationFieldBuilder.jsx
//
// CRUD panel for custom registration form fields on a ticketed event item
// (server/routes/ticketingRoutes.js — t_registration_field_def). Rendered as
// a tab inside TicketConfig.jsx.

import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const FIELD_TYPES = ["text", "textarea", "select", "checkbox", "date", "phone", "email"];

const EMPTY_FIELD = {
  field_key: "",
  label: "",
  field_type: "text",
  required: false,
  options: "",
};

export default function RegistrationFieldBuilder({ tenantId, eventId, itemId }) {
  const base = `/api/ticketing/${tenantId}/event/${eventId}/item/${itemId}`;

  const [fields, setFields] = useState([]);
  const [form, setForm] = useState(EMPTY_FIELD);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const loadFields = useCallback(async () => {
    try {
      const { data } = await axios.get(`${base}/fields`);
      if (data.success) setFields(data.fields);
    } catch (err) {
      console.error("Error loading registration fields:", err);
    }
  }, [base]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  const submitField = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ text: "", type: "" });

    const payload = {
      field_key: form.field_key.trim(),
      label: form.label.trim(),
      field_type: form.field_type,
      required: !!form.required,
      options:
        form.field_type === "select" && form.options.trim()
          ? form.options.split(",").map((s) => s.trim()).filter(Boolean)
          : undefined,
    };

    try {
      const { data } = await axios.post(`${base}/fields`, payload);
      if (data.success) {
        setMessage({ text: data.message, type: "success" });
        setForm(EMPTY_FIELD);
        loadFields();
      }
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || "Failed to create field", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const deleteField = async (fieldId) => {
    if (!window.confirm("Delete this registration field?")) return;
    try {
      await axios.delete(`${base}/fields/${fieldId}`);
      loadFields();
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || "Failed to delete field", type: "error" });
    }
  };

  return (
    <div>
      {message.text && (
        <div
          className={`mb-4 px-3 py-2 rounded text-sm ${
            message.type === "error"
              ? "bg-red-50 text-red-700 border border-red-200"
              : "bg-emerald-50 text-emerald-700 border border-emerald-200"
          }`}
        >
          {message.text}
        </div>
      )}

      <form
        onSubmit={submitField}
        className="rounded-xl border border-black/10 dark:border-white/10 p-4 mb-4 grid grid-cols-2 gap-3"
      >
        <input
          className="px-3 py-2 rounded border bg-transparent"
          placeholder="Field key (e.g. dietary_restrictions)"
          value={form.field_key}
          onChange={(e) => setForm((f) => ({ ...f, field_key: e.target.value }))}
          pattern="[a-zA-Z0-9_]+"
          title="Letters, numbers, and underscores only"
          required
        />
        <input
          className="px-3 py-2 rounded border bg-transparent"
          placeholder="Label shown to attendees"
          value={form.label}
          onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
          required
        />
        <select
          className="px-3 py-2 rounded border bg-transparent"
          value={form.field_type}
          onChange={(e) => setForm((f) => ({ ...f, field_type: e.target.value }))}
        >
          {FIELD_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        {form.field_type === "select" && (
          <input
            className="px-3 py-2 rounded border bg-transparent"
            placeholder="Comma-separated choices"
            value={form.options}
            onChange={(e) => setForm((f) => ({ ...f, options: e.target.value }))}
          />
        )}
        <label className="col-span-2 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.required}
            onChange={(e) => setForm((f) => ({ ...f, required: e.target.checked }))}
          />
          Required field
        </label>
        <div className="col-span-2">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded bg-cobalt text-white text-sm font-medium disabled:opacity-50"
          >
            {saving ? "Saving…" : "Add Field"}
          </button>
        </div>
      </form>

      {fields.length === 0 ? (
        <div className="text-sm opacity-70">No custom registration fields yet.</div>
      ) : (
        <ul className="space-y-2">
          {fields.map((field) => (
            <li
              key={field.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-black/10 dark:border-white/10 p-3 text-sm"
            >
              <div>
                <div className="font-semibold">
                  {field.label} {field.required ? <span className="text-red-500">*</span> : null}
                </div>
                <div className="opacity-70">
                  {field.field_key} · {field.field_type}
                  {field.eventitemid == null ? " · event-wide" : ""}
                </div>
              </div>
              <button
                onClick={() => deleteField(field.id)}
                className="px-2 py-1 rounded border hover:bg-black/5 dark:hover:bg-white/10 text-red-600"
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
